import React, { useState } from 'react'
import { ExternalLink, Shield, Cloud } from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * Customer-Facing Google Drive Connect for Onboarding
 * Simple, clean interface without technical setup details
 */
const CustomerGoogleDriveConnect = ({ onConnectionSuccess, onConnectionError }) => {
  const [isConnecting, setIsConnecting] = useState(false)

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const isConfigured = clientId && 
    clientId !== 'your-google-client-id' &&
    clientId.includes('.apps.googleusercontent.com')

  // Debug logging
  console.log('🔍 Google Client ID Debug:')
  console.log('- Raw value:', clientId)
  console.log('- Is configured:', isConfigured)
  console.log('- All env vars:', import.meta.env)

  const handleConnect = async () => {
    if (!isConfigured) {
      toast.error('Google Drive is not available yet. Please try again later.')
      return
    }

    // Skip connection during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (!isOnboarding) {
      toast.error('Google Drive connection not available outside of onboarding')
      return
    }

    setIsConnecting(true)

    try {
      // Use standard OAuth flow instead of popup
      const scope = 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email'
      const redirectUri = `${window.location.origin}/auth/google/callback`
      
      // Generate OAuth state with user info
      let stateParam = ''
      const authToken = localStorage.getItem('authToken')
      const user = localStorage.getItem('user')
      
      console.log('🔍 CustomerGoogleDriveConnect: Generating OAuth state...')
      console.log('🔍 CustomerGoogleDriveConnect: authToken exists:', !!authToken)
      console.log('🔍 CustomerGoogleDriveConnect: user exists:', !!user)
      console.log('🔍 CustomerGoogleDriveConnect: All localStorage keys:', Object.keys(localStorage))
      
      // Try to get user info from JWT if user key is missing
      let userEmail = null
      if (!user && authToken) {
        console.log('🔍 CustomerGoogleDriveConnect: User key missing, attempting to decode JWT token...')
        try {
          const tokenParts = authToken.split('.')
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]))
            console.log('🔍 CustomerGoogleDriveConnect: Decoded JWT payload:', payload)
            if (payload.email) {
              userEmail = payload.email
              console.log('✅ CustomerGoogleDriveConnect: Found user email in JWT:', userEmail)
            }
          }
        } catch (jwtError) {
          console.warn('⚠️ CustomerGoogleDriveConnect: Failed to decode JWT token:', jwtError)
        }
      }
      
      if (authToken && (user || userEmail)) {
        try {
          let userData
          if (user) {
            userData = JSON.parse(user)
          } else {
            // Create user data from JWT
            userData = { email: userEmail }
          }
          
          console.log('🔍 CustomerGoogleDriveConnect: Using user data:', userData)
          
          const stateData = {
            userEmail: userData.email,
            timestamp: Date.now(),
            sessionId: btoa(authToken.substring(0, 32)).substring(0, 16)
          }
          const stateJson = JSON.stringify(stateData)
          stateParam = `&state=${encodeURIComponent(stateJson)}`
          console.log('🔍 CustomerGoogleDriveConnect: Generated state data:', stateData)
          console.log('🔍 CustomerGoogleDriveConnect: State JSON:', stateJson)
          console.log('🔍 CustomerGoogleDriveConnect: Generated state param:', stateParam)
          console.log('🔍 CustomerGoogleDriveConnect: State param length:', stateParam.length)
        } catch (error) {
          console.warn('⚠️ CustomerGoogleDriveConnect: Failed to parse user data for OAuth state:', error)
          console.error('⚠️ CustomerGoogleDriveConnect: Raw user data:', user)
        }
      } else {
        console.warn('⚠️ CustomerGoogleDriveConnect: Missing auth data for OAuth state')
        console.warn('⚠️ CustomerGoogleDriveConnect: authToken:', !!authToken, 'user:', !!user, 'userEmail from JWT:', !!userEmail)
      }
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        `access_type=offline&` +
        `prompt=consent${stateParam}`

      // Redirect to Google OAuth
      window.location.href = authUrl

    } catch (error) {
      console.error('Error starting OAuth:', error)
      toast.error('Failed to start connection. Please try again.')
      onConnectionError?.(error.message)
      setIsConnecting(false)
    }
  }

  if (!isConfigured) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Cloud className="w-6 h-6 text-yellow-600" />
        </div>
        <h3 className="text-sm font-medium text-yellow-800 mb-2">
          Google Drive Coming Soon
        </h3>
        <p className="text-sm text-yellow-700">
          Google Drive integration is being set up. You can continue and connect later in Settings.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
          <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24">
            <path fill="currentColor" d="M6.28 3l5.24 9.07L15.76 3z"/>
            <path fill="currentColor" d="M23.44 12l-5.24-9.07H23.44z"/>
            <path fill="currentColor" d="M.56 12l5.24 9.07H.56z"/>
            <path fill="currentColor" d="M12 13.93L6.76 21H23.44z"/>
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900">Google Drive</h3>
          <p className="text-sm text-gray-500">Secure cloud storage</p>
        </div>
      </div>

      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className={`w-full px-4 py-3 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 ${
          isConnecting 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isConnecting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <ExternalLink className="w-4 h-4" />
            <span>Connect Google Drive</span>
          </>
        )}
      </button>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <div className="flex items-center justify-center space-x-2">
          <Shield className="w-3 h-3" />
          <span>Your data stays in your Google Drive</span>
        </div>
      </div>
    </div>
  )
}

export default CustomerGoogleDriveConnect 